import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { memo } from "react";

import Translations from "@/components/Core/Translations";
import { useTranslate } from "@/hooks";
import { gradientTextSx } from "@/theme";

import { EMPTY_MASCOT_AVATAR_SRC, EMPTY_MASCOT_SRC, EMPTY_MIN_HEIGHT } from "./data";
import { MascotFrame, MascotImage } from "./styles";
import { EmptyStateProps } from "./type";

/**
 * What every blank screen in the app looks like.
 *
 * There were four different answers to "there is nothing here": a grey
 * document glyph on the game grid, two lines of muted text in the bet slip, a
 * single centred sentence in the notification tray, and a 404 illustration
 * left over from another brand. They are one thing now, and that thing is the
 * mascot - which is also the only way an empty page says whose page it is.
 */
const EmptyState = ({
  size = "compact",
  title,
  description,
  eyebrow,
  action,
  minHeight,
}: EmptyStateProps) => {
  const translate = useTranslate();
  const isPage = size === "page";

  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      textAlign="center"
      gap={isPage ? 1.5 : 1}
      sx={{ width: "100%", p: isPage ? 3 : 2, minHeight: minHeight ?? EMPTY_MIN_HEIGHT[size] }}
    >
      {eyebrow && (
        <Typography
          component="p"
          variant="h1"
          sx={{ fontSize: "clamp(3rem, 14vw, 5.5rem)", lineHeight: 1, ...gradientTextSx }}
        >
          {eyebrow}
        </Typography>
      )}

      <MascotFrame size={size}>
        <MascotImage
          src={isPage ? EMPTY_MASCOT_SRC : EMPTY_MASCOT_AVATAR_SRC}
          alt={translate("mascot.image-alt")}
          loading="lazy"
          decoding="async"
          round
        />
      </MascotFrame>

      <Stack gap={0.5} alignItems="center">
        <Typography variant={isPage ? "h4" : "subtitle1"} component="p">
          <Translations text={title} />
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380 }}>
            <Translations text={description} />
          </Typography>
        )}
      </Stack>

      {action}
    </Stack>
  );
};

export default memo(EmptyState);
